// Admin → Users API client. Lists the site's project members (the full
// upstream roster, 1:1 with the source Users page); profile fields come from
// that roster. Deliberately separate from the comms /members picker, which is
// a temporary route that will be scoped down when per-site membership lands.

import { authedFetch, UnauthorizedError } from '@apis/auth';

export interface IAdminUser {
  id: string;
  username: string;
  name?: string;
  email?: string;
  phone?: string;
  /** Upstream role constant, e.g. `PROJECT_ADMIN` — map to a label for display. */
  role?: string;
  /** Professions a VIEWER is permitted to see — absent for other roles. */
  permittedProfessionsIds?: string[];
  /**
   * How the member signs in, derived server-side from the email domain. `sso`
   * members have no password, so no reset is offered. Optional: an older
   * backend omits it, which reads as `password`.
   */
  signInMethod?: 'password' | 'sso';
  /** The member's SSO connection's name (e.g. "DAITEC"); null for password members or when unknown. */
  ssoConnectionName?: string | null;
  // The sign-in facts below decorate the roster. null/absent means UNKNOWN — the
  // backend could not read them (or is older) — and is shown as "—", never "no".
  /** `active`: has signed in. `invited`: not yet, invitation live. `invitationExpired`: resend it. */
  status?: AdminUserStatus | null;
  /** ISO instant the set-password link expires — a live password invitation only. */
  invitationExpiresAt?: string | null;
  /** `identityProvider`: an SSO member — the company's IdP owns the second factor. */
  mfa?: AdminUserMfa | null;
  /** Enrolled Trusstor MFA methods (empty unless `mfa === 'enabled'`). */
  mfaMethods?: AdminUserMfaMethod[];
}

export type AdminUserStatus = 'active' | 'invited' | 'invitationExpired';
export type AdminUserMfa = 'enabled' | 'disabled' | 'identityProvider';
export type AdminUserMfaMethod = 'totp' | 'email' | 'sms' | 'line';

/** Backend answered 409 USER_CREATE_CONFLICT — the email is already registered
 *  upstream (their known case: a viewer in a different project). */
export class UserCreateConflictError extends Error {
  constructor() {
    super('a user with this email already exists');
    this.name = 'UserCreateConflictError';
  }
}

async function parseErrors(res: Response): Promise<never> {
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  const error = body?.error as Record<string, unknown> | undefined;
  if (res.status === 401) throw new UnauthorizedError();
  if (res.status === 409 && error?.code === 'USER_CREATE_CONFLICT') {
    throw new UserCreateConflictError();
  }
  throw new Error(`request failed: ${res.status} ${(error?.message as string | undefined) ?? ''}`);
}

export async function fetchAdminUsers(
  siteId: string,
  signal?: AbortSignal,
): Promise<{ users: IAdminUser[] }> {
  const res = await authedFetch(`/api/sites/${encodeURIComponent(siteId)}/admin/users`, {
    signal,
  });
  if (res.ok) return (await res.json()) as { users: IAdminUser[] };
  return parseErrors(res);
}

/** The project-scoped roles an admin may hand out — backoffice-only roles excluded. */
export type AdminUserRole = 'PROJECT_ADMIN' | 'MANAGER' | 'VIEWER';

/** Upstream UI-language constants (Trusstor's preferences store). */
export type AdminUserLanguage = 'en-us' | 'jpn' | 'he-il';

// The ordinary (profile) user edit — identity (username/email), credentials
// and membership are not editable through this surface. `phone: null` clears
// the stored number.
export interface IUpdateAdminUserInput {
  name?: string;
  phone?: string | null;
  language?: AdminUserLanguage;
  role?: AdminUserRole;
  permittedProfessionsIds?: string[];
}

// The register-with-invite create — the account is created upstream
// immediately (username = email); the invite email only gates first login.
export interface ICreateAdminUserInput {
  name: string;
  email: string;
  phone: string | null;
  language?: AdminUserLanguage;
  role: AdminUserRole;
  permittedProfessionsIds?: string[];
}

export async function createAdminUser(
  siteId: string,
  input: ICreateAdminUserInput,
): Promise<{ user: { id: string | null; username: string } }> {
  const res = await authedFetch(`/api/sites/${encodeURIComponent(siteId)}/admin/users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (res.ok) return (await res.json()) as { user: { id: string | null; username: string } };
  return parseErrors(res);
}

export async function updateAdminUser(
  siteId: string,
  username: string,
  input: IUpdateAdminUserInput,
): Promise<void> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/admin/users/${encodeURIComponent(username)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    },
  );
  if (res.ok) return;
  return parseErrors(res);
}

/** Removes the member from the site's project — their account goes too when it was their last one. */
export async function deleteAdminUser(siteId: string, username: string): Promise<void> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/admin/users/${encodeURIComponent(username)}`,
    { method: 'DELETE' },
  );
  if (res.ok) return;
  return parseErrors(res);
}

/** Mails the member a password-reset link (to their address on record). */
export async function sendAdminUserPasswordReset(siteId: string, username: string): Promise<void> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/admin/users/${encodeURIComponent(username)}/password-reset`,
    { method: 'POST' },
  );
  if (res.ok) return;
  return parseErrors(res);
}

/** Re-sends the member's invitation mail (to their address on record). */
export async function resendAdminUserInvitation(siteId: string, username: string): Promise<void> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/admin/users/${encodeURIComponent(username)}/invitations`,
    { method: 'POST' },
  );
  if (res.ok) return;
  return parseErrors(res);
}

/** Stored UI language for the edit dialog's prefill; null when never saved. */
export async function fetchAdminUserLanguage(
  siteId: string,
  username: string,
  signal?: AbortSignal,
): Promise<string | null> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/admin/users/${encodeURIComponent(username)}/language`,
    { signal },
  );
  if (res.ok) return ((await res.json()) as { language: string | null }).language;
  return parseErrors(res);
}
